/**
 * useTutorLiveCenterMaterial — ce que le centre fait autour du support de l'activité (le `material`) : le
 * visuel du tableau blanc, et l'aide à la correction sous l'énoncé.
 *
 * Le visuel est l'ancien `MaterialExplanationDisplay` (monté sans rendu par `WhiteboardTab`) :
 *  - « Générer visuel » → `generateVisualExplanation({ material_id })`, mesuré
 *    `button.clicked.whiteboard-generate-visual` (en présentation) ou `…-preshare` (avant). La préparation se
 *    voit dès le clic, pas au retour de la demande (weekly du 03/11) ; et la latence se mesure en deux temps
 *    (`whiteboard.visual.ready` / `…timed_out` / `…request_failed` : `request_ms`, le temps de la demande, et
 *    `total_ms`, du clic au visuel), pour savoir d'où elle vient ;
 *  - puis `getMaterial({ id })` toutes les 5 s, et au bout de 90 s « Visuel non disponible » ;
 *  - l'avis du masteur relu (`getMaterialFeedback`, `feature_type: visual_explanation`) une fois le visuel
 *    là, et envoyé (`submitMaterialFeedback`, `comment: null`).
 * Une demande refusée ne s'affiche pas, comme l'ancien (son message restait dans l'état, non rendu).
 *
 * L'aide à la correction est l'ancien `MaterialCorrectionDisplay`, côté tuteur, sous un énoncé écrit :
 *  - son statut vient du support (`correction_status`, à défaut le marqueur « ... ») ;
 *  - tant qu'elle se prépare, `getMaterial` toutes les 5 s, abandon à 180 s (« Réessayer ») — une réponse
 *    « en cours » tardive ne ramène pas l'attente, seul « Réessayer » le fait ;
 *  - « Réessayer » → `retryMaterialCorrection({ material_id })` ;
 *  - l'avis relu (`feature_type: correction`) et envoyé ; mesures `button.clicked.material-correction-*`.
 *
 * Chaque pouce mesure aussi `checkpoint.feedback.button_click`, comme le bouton d'avis de l'ancien
 * (`FeedbackButton`, C-94).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import { apiCall, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type UICorrectionFeedback } from "../../../types/studentActivity"
import { type UITutorStatementAid, type UITutorWhiteboardVisual } from "../../../types/tutorLive"
import {
    liveAidMarkdown,
    liveAidStatus,
    type ApiLiveMaterial,
    type LiveAidStatus,
} from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"

export const MATERIAL_POLL_MS = 5000
export const VISUAL_TIMEOUT_MS = 90000
export const AID_TIMEOUT_MS = 180000

type Feedback = { feedback_type?: string | null } | null

const feedbackOf = (answer: unknown): UICorrectionFeedback | null => {
    const fresh = ((answer as { data?: Feedback })?.data ?? answer) as Feedback
    const type = fresh?.feedback_type
    return type === "positive" || type === "negative" ? type : null
}
const materialOf = (answer: unknown) =>
    ((answer as { data?: ApiLiveMaterial })?.data ?? answer) as ApiLiveMaterial | null

/** Un visuel déjà fait sur le support (`visual_explanation[0]`). */
const visualUrlOf = (material: ApiLiveMaterial | null | undefined) =>
    material?.visual_explanation != null && material.visual_explanation.length > 0
        ? material.visual_explanation[0]
        : null

export type UseTutorLiveCenterMaterialArgs = {
    material: ApiLiveMaterial | null | undefined
    /** Une présentation tourne (la mesure de « Générer visuel » le dit). */
    presenting: boolean
    /** Le tableau blanc est offert : le visuel n'a de sens que là. */
    whiteboard: boolean
    /** L'énoncé est écrit : l'aide à la correction se pose dessous. */
    writtenStatement: boolean
}

export type TutorLiveCenterMaterial = {
    visual?: UITutorWhiteboardVisual
    onGenerateVisual: () => void
    onToggleVisual: () => void
    onVisualFeedback: (feedback: UICorrectionFeedback) => void
    statementAid?: UITutorStatementAid
    onToggleAid: () => void
    onRetryAid: () => void
    onAidFeedback: (feedback: UICorrectionFeedback) => void
}

export function useTutorLiveCenterMaterial(args: UseTutorLiveCenterMaterialArgs): TutorLiveCenterMaterial {
    const { material, presenting, whiteboard, writtenStatement } = args
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    const materialId = material?.id ?? null
    const call = useCallback(
        <T = unknown>(service: unknown, params: Parameters<typeof apiCall>[2] = {}) =>
            apiCall<T>(dispatch, service as ApiService, params),
        [dispatch]
    )

    // ── Le visuel du tableau blanc ─────────────────────────────────────────────────────────────────
    const [visualUrl, setVisualUrl] = useState<string | null>(visualUrlOf(material))
    const [polling, setPolling] = useState(false)
    // La demande du visuel est partie, sans réponse encore : la préparation se voit dès le clic.
    const [requesting, setRequesting] = useState(false)
    // Les instants d'une demande, pour mesurer sa latence une fois (prêt, abandon ou refus).
    const visualTimingRef = useRef<{ askedAt: number; answeredAt: number | null } | null>(null)
    const measureVisual = useCallback((event: string) => {
        const timing = visualTimingRef.current
        if (timing == null) return
        visualTimingRef.current = null
        const now = Date.now()
        amplitude.track(event, {
            request_ms: (timing.answeredAt ?? now) - timing.askedAt,
            total_ms: now - timing.askedAt,
        })
    }, [])
    const [timedOut, setTimedOut] = useState(false)
    const [shown, setShown] = useState(true)
    const [visualFeedback, setVisualFeedback] = useState<UICorrectionFeedback | null>(null)

    // Un autre support : on repart de ce qu'il porte.
    useEffect(() => {
        setVisualUrl(visualUrlOf(material))
        setPolling(false)
        setTimedOut(false)
        setShown(true)
        setVisualFeedback(null)
        setRequesting(false)
        visualTimingRef.current = null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialId])
    const servedUrl = visualUrlOf(material)
    useEffect(() => {
        if (servedUrl != null) setVisualUrl(servedUrl)
    }, [servedUrl])

    useEffect(() => {
        if (!polling || materialId == null) return
        const poll = () =>
            call(activitiesService.getMaterial, { routeParams: { id: materialId } })
                .then((answer) => {
                    const fresh = materialOf(answer)
                    const url = visualUrlOf(fresh)
                    if (fresh?.id === materialId && url != null) {
                        setVisualUrl(url)
                        setPolling(false)
                        measureVisual("whiteboard.visual.ready")
                    }
                })
                // Un relevé en échec : on continue (l'ancien aussi).
                .catch(() => undefined)
        poll()
        const interval = setInterval(poll, MATERIAL_POLL_MS)
        const timeout = setTimeout(() => {
            setPolling(false)
            setTimedOut(true)
            measureVisual("whiteboard.visual.timed_out")
        }, VISUAL_TIMEOUT_MS)
        return () => {
            clearInterval(interval)
            clearTimeout(timeout)
        }
    }, [polling, materialId, call, measureVisual])

    // L'avis déjà donné sur ce visuel.
    useEffect(() => {
        if (materialId == null || visualUrl == null || !whiteboard) return
        let current = true
        call(activitiesService.getMaterialFeedback, {
            routeParams: { material_id: materialId, feature_type: "visual_explanation" },
        })
            .then((answer) => current && setVisualFeedback(feedbackOf(answer)))
            .catch(() => current && setVisualFeedback(null))
        return () => {
            current = false
        }
    }, [materialId, visualUrl, whiteboard, call])

    const onGenerateVisual = useCallback(() => {
        if (materialId == null) return
        amplitude.track(`button.clicked.whiteboard-generate-visual${presenting ? "" : "-preshare"}`)
        setTimedOut(false)
        // La préparation se voit dès le clic ; l'attente des 90 s, elle, part au retour de la demande.
        setRequesting(true)
        const timing = { askedAt: Date.now(), answeredAt: null as number | null }
        visualTimingRef.current = timing
        call(activitiesService.generateVisualExplanation, { routeParams: { material_id: materialId } })
            .then(() => {
                timing.answeredAt = Date.now()
                setPolling(true)
            })
            .catch(() => measureVisual("whiteboard.visual.request_failed"))
            .finally(() => setRequesting(false))
    }, [call, materialId, presenting, measureVisual])

    const onVisualFeedback = useCallback(
        (feedback: UICorrectionFeedback) => {
            if (materialId == null) return
            amplitude.track("checkpoint.feedback.button_click")
            call(activitiesService.submitMaterialFeedback, {
                routeParams: {
                    material_id: materialId,
                    feedback_type: feedback,
                    feature_type: "visual_explanation",
                    comment: null,
                },
            })
                .then((answer) => setVisualFeedback(feedbackOf(answer) ?? feedback))
                .catch(() => undefined)
        },
        [call, materialId]
    )

    const visual: UITutorWhiteboardVisual | undefined = !whiteboard
        ? undefined
        : visualUrl != null
          ? { state: "ready", uri: visualUrl, shown, feedback: visualFeedback }
          : polling || requesting
            ? { state: "generating" }
            : timedOut
              ? { state: "unavailable" }
              : undefined

    // ── L'aide à la correction ─────────────────────────────────────────────────────────────────────
    const [aidStatus, setAidStatus] = useState<LiveAidStatus>(liveAidStatus(material))
    const [aidMarkdown, setAidMarkdown] = useState(liveAidMarkdown(material))
    const [aidOpen, setAidOpen] = useState(false)
    const [retrying, setRetrying] = useState(false)
    const [aidFeedback, setAidFeedback] = useState<UICorrectionFeedback | null>(null)
    const gaveUpRef = useRef(false)

    const applyMaterial = useCallback((fresh: ApiLiveMaterial | null | undefined) => {
        const next = liveAidStatus(fresh)
        if (!(gaveUpRef.current && next === "generating")) setAidStatus(next)
        const markdown = liveAidMarkdown(fresh)
        if (markdown !== "") setAidMarkdown(markdown)
    }, [])

    useEffect(() => {
        gaveUpRef.current = false
        setAidOpen(false)
        setRetrying(false)
        setAidFeedback(null)
        setAidStatus(liveAidStatus(material))
        setAidMarkdown(liveAidMarkdown(material))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialId])
    const servedAid = `${material?.correction_status ?? ""}|${material?.correction_markdown ?? ""}`
    useEffect(() => {
        applyMaterial(material)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servedAid])

    const aidActive = writtenStatement && materialId != null
    useEffect(() => {
        if (!aidActive || aidStatus !== "generating") return
        const poll = () =>
            call(activitiesService.getMaterial, { routeParams: { id: materialId } })
                .then((answer) => {
                    const fresh = materialOf(answer)
                    if (fresh?.id === materialId) applyMaterial(fresh)
                })
                .catch(() => undefined)
        poll()
        const interval = setInterval(poll, MATERIAL_POLL_MS)
        const cap = setTimeout(() => {
            gaveUpRef.current = true
            setAidStatus("failed")
        }, AID_TIMEOUT_MS)
        return () => {
            clearInterval(interval)
            clearTimeout(cap)
        }
    }, [aidActive, aidStatus, materialId, call, applyMaterial])

    // L'avis déjà donné sur le corrigé (l'ancien le lit au montage, quand le corrigé est là).
    const aidReady = aidActive && aidMarkdown !== ""
    useEffect(() => {
        if (!aidReady || materialId == null) return
        let current = true
        call(activitiesService.getMaterialFeedback, {
            routeParams: { material_id: materialId, feature_type: "correction" },
        })
            .then((answer) => current && setAidFeedback(feedbackOf(answer)))
            .catch(() => current && setAidFeedback(null))
        return () => {
            current = false
        }
    }, [aidReady, materialId, call])

    const onToggleAid = useCallback(() => {
        amplitude.track("button.clicked.material-correction-display-button")
        setAidOpen((open) => !open)
    }, [])
    const onRetryAid = useCallback(() => {
        if (materialId == null || retrying) return
        amplitude.track("button.clicked.material-correction-retry-button")
        gaveUpRef.current = false
        setRetrying(true)
        call(activitiesService.retryMaterialCorrection, { routeParams: { material_id: materialId } })
            .then((answer) => {
                const fresh = materialOf(answer)
                if (fresh?.id === materialId) applyMaterial(fresh)
            })
            .catch(() => undefined)
            .finally(() => setRetrying(false))
    }, [applyMaterial, call, materialId, retrying])
    const onAidFeedback = useCallback(
        (feedback: UICorrectionFeedback) => {
            if (materialId == null) return
            amplitude.track("checkpoint.feedback.button_click")
            call(activitiesService.submitMaterialFeedback, {
                routeParams: {
                    material_id: materialId,
                    feedback_type: feedback,
                    feature_type: "correction",
                    comment: null,
                },
            })
                .then((answer) => setAidFeedback(feedbackOf(answer) ?? feedback))
                .catch(() => undefined)
        },
        [call, materialId]
    )

    const statementAid: UITutorStatementAid | undefined = !aidActive
        ? undefined
        : aidStatus === "generating"
          ? { state: "generating" }
          : aidStatus === "failed"
            ? { state: "failed", retrying }
            : aidStatus === "ready" && aidMarkdown !== ""
              ? { state: "ready", open: aidOpen, markdown: aidMarkdown, feedback: aidFeedback }
              : undefined

    return {
        visual,
        onGenerateVisual,
        onToggleVisual: useCallback(() => setShown((on) => !on), []),
        onVisualFeedback,
        statementAid,
        onToggleAid,
        onRetryAid,
        onAidFeedback,
    }
}
